import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDrop } from 'react-dnd'
import dndItemTypes from '../../../../constants/dndItemTypes.jsx'
import ImagesMovieDisplayAndUploadItem from './ImagesMovieDisplayAndUploadItem.jsx'
import ImageUpload from '../ImagesGeneral/ImageUpload/index.jsx'


const ImagesMovieDisplayAndUploadList = memo(function ImagesMovieDisplayAndUploadList({
    dndItems,
    movieTitle, 
    isUploading, 
    isDisabled,
    imageType,
    onUploadStart, 
    onUploadEndSuccess, 
    onUploadEndError,
    onResetErrorMessages,
    setMovieImages
}) {
    const [items, setItems] = useState(dndItems)

    const deepCopy = a => JSON.parse(JSON.stringify(a))


    useEffect(() => {
        setItems(dndItems);
    }, [dndItems])


    useEffect(() => {
        setMovieImages(deepCopy(items))
    }, [items])

    const findItem = useCallback(
        (itemId) => {
          const item = items.filter((i) => `${i.itemId}` === itemId)[0]

            return {
                item,
                index: items.indexOf(item)
            }
        },
        [items]
    )

    const moveItem = useCallback(
        (itemId, atIndex) => {
            const { item, index } = findItem(itemId)


            const copyItems = deepCopy(items)

            copyItems.splice(index, 1)
            copyItems.splice(atIndex, 0, item)

            setItems(copyItems)
        },
        [findItem, items, setItems],
    )


    const changeItem = useCallback(
        (index, key) => (fieldName, value) => {
            const copyItems = deepCopy(items)

            copyItems[index][key] = value

            setItems(copyItems)
        },
        [items, setItems]
    )

    const addItem = useCallback(
        (data, replaceAtIndex = false)  => {
            if(!data.hasOwnProperty('id') ||!data.hasOwnProperty('alt_text') || !data.hasOwnProperty('source_url')) {
                onUploadEndError('OnUploadEndSuccess: Missing data attributes')
            } else {
                const copyItems = deepCopy(items)
                const itemsLength = copyItems.length
                const replaceImg = false !== replaceAtIndex
    
                let newImageObject = {
                    id: data.id,
                    itemId: data.id.toString(),
                    format: data.hasOwnProperty('format')  && data.format ? data.format : 'none',
                    alt: data.alt_text,
                    url: data.source_url,
                    position: '',
                    positionY: '',
                    positionX: '',
                    text: replaceImg ? replaceAtIndex : itemsLength
                }
    
                if(replaceImg) {
                    copyItems[replaceAtIndex] = newImageObject
                } else {
                    copyItems.push(newImageObject)
                }
    
                setItems(copyItems)
                onUploadEndSuccess()
            }
        },
        [items, setItems, onUploadEndError, onUploadEndSuccess]
    )

    const deleteItem = useCallback(
        (itemId) => {
            setItems(items.filter(i => itemId !== i.itemId ))
        },
        [items, setItems]
    )

    const [, drop] = useDrop(() => ({ accept: dndItemTypes.MOVIE_IMAGE }))


    const getHeadingTransId = useCallback(
        (index) => {
            const i = index > 3 ? 3 : index
            const imagesLength = items.length 
            const headings = []


            if(2 < imagesLength) {
                headings.push('images_movie_description_first_image')
                headings.push('images_movie_description_second_image')
                headings.push('images_movie_description_third_image')
                headings.push('images_movie_description_unused_image')
            } else if(2 === imagesLength) { 
                headings.push('images_movie_description_first_image')
                headings.push('images_movie_description_second_image_of_two')
            } else if(1 === imagesLength) {
                headings.push('images_movie_description_first_image_of_one') 
            }

            return headings[i]
        },
        [items]
    )


    return (
        <>
            <div ref={drop} className={"images-grid" + (0 !== items.length ? " has-images" : "")}>
                {items.map((img, index) => {

                    const fieldName = 0 === index ? "movie.images.ambient.position"  : "movie.images.backgrounds[" + parseInt(index - 1) + "].position"
                    
                    return (
                        <ImagesMovieDisplayAndUploadItem
                            key={index}
                            itemId={`${img.itemId}`}
                            index={index}
                            fieldName={fieldName}
                            image={img}
                            movieTitle={movieTitle}
                            imageType={imageType}
                            isUploading={isUploading}
                            onUploadStart={onUploadStart} 
                            onUploadEndSuccess={addItem}
                            onUploadEndError={onUploadEndError}
                            onDeleteStart={deleteItem}
                            onResetErrorMessages={onResetErrorMessages}
                            findItem={findItem}
                            moveItem={moveItem}
                            getHeadingTransId={getHeadingTransId}
                            changeItem={changeItem}
                        />
                    )
                })}
            </div>

            <div className="image-grid-item images-grid-item-upload">
                <ImageUpload
                    isDisabled={isDisabled}
                    isUploading={isUploading}
                    onUploadStart={onUploadStart}
                    onUploadEndSuccess={addItem}
                    onUploadEndError={onUploadEndError}
                    onResetErrorMessages={onResetErrorMessages}
                />
            </div>
        </>

    )
})

export default ImagesMovieDisplayAndUploadList